import Vue from "vue";
import { Component } from "vue-property-decorator";
import { handleError, initClient } from "../../../api";
import { fixISettings, WindowType } from "../../../util";
import { PivotClient, PivotSettingsClient, PivotTemplate, PivotTemplates, SaveTemplateInSubAccountsRequest, SettingsApiControllerUIClient } from '../../backend/backendapi';
import CreateTemplateModal from "./createTemplateModal";
import SelectTemplateModal from "./selectTemplateModal/selectTemplateModal.controller";
import Visible from "../../../directives/visible";

declare var window: WindowType & {
    $pvtBuilder: any,
};

@Component({
    name: 'PivotTemplatesComponent',
    components: {
        CreateTemplateModal,
        SelectTemplateModal: require('./selectTemplateModal/selectTemplateModal.vue.html').default,
    },
    directives: {
        Visible
    },
})
export default class PivotTemplatesComponent extends Vue {
    //data
    private templates: PivotTemplates | null = null;
    private templatesSharedOwn: PivotTemplates | null = null;
    private templatesSharedOther: PivotTemplates | null = null;
    private readonly firstXTemplatesCount = 10;

    //api
    private readonly pivotApi = initClient(PivotClient);
    private readonly settingsAPI = initClient(SettingsApiControllerUIClient);
    private readonly pivotSettingsAPI = initClient(PivotSettingsClient);

    constructor() {
        super();
        fixISettings();
    }

    mounted(): void {
        this.reloadTemplates();
    }

    //data load
    private async reloadTemplates(): Promise<void> {
        const set = (await this.settingsAPI.get('PivotTemplates', false, false)).result!;
        this.templates = new PivotTemplates();
        this.templates.init(set);

        const sharedOwn = (await this.settingsAPI.get('PivotTemplates', true, false)).result!;
        this.templatesSharedOwn = new PivotTemplates();
        this.templatesSharedOwn.init(sharedOwn);

        this.templatesSharedOther = await this.pivotSettingsAPI.shared();
    }

    //data save
    private async saveTemplates(shared: boolean): Promise<void> {
        const templates = shared ? this.templatesSharedOwn : this.templates;
        await this.settingsAPI.update('PivotTemplates', shared, false, JSON.stringify(templates))
            .then(_ => this.$toasted.info(this.$t('txtSaved').toString()))
            .catch(e => handleError(this.$toasted, this.$i18n, e));
    }

    private async saveTemplateInSubAccounts(template: PivotTemplate, sharedWithAccounts: number[]): Promise<void> {
        await this.pivotSettingsAPI.saveTemplateInSubAccounts(SaveTemplateInSubAccountsRequest.fromJS({ template: template, accountIds: sharedWithAccounts }))
            .then(_ => this.$toasted.info(this.$t('txtSaved').toString()))
            .catch(e => handleError(this.$toasted, this.$i18n, e));
    }

    //functions
    private openSelectTemplateModal(): void {
        (this.$refs.selectTemplateModal as SelectTemplateModal).openModal();
    }

    private createTemplate(): void {
        if (!this.templates) {
            return;
        }
        (this.$refs.createTemplateModal as CreateTemplateModal).openModal();
    }

    //event handler
    private onTemplateCreated(event: { name: string, shared: boolean, sharedWithAccounts: number[] }): void {
        if (!this.templates || !this.templatesSharedOwn) {
            return;
        }
        let json = this.getPivotTableConfigJson();
        let template = new PivotTemplate({ name: event.name, json: json });
        if (!event.shared) {
            this.templates.templates.push(template);
        } else {
            this.templatesSharedOwn.templates.push(template);
        }
        this.saveTemplates(event.shared);
        if (event.shared && event.sharedWithAccounts.length > 0) {
            this.saveTemplateInSubAccounts(template, event.sharedWithAccounts);
        }
        (this.$refs.createTemplateModal as CreateTemplateModal).closeModal();
    }

    private async onTemplateSelected(template: PivotTemplate): Promise<void> {
        this.setPivotTableConfigJson(template.json);
    }

    private async onTemplateDeleted(template: PivotTemplate): Promise<void> {
        let index = this.templates?.templates.findIndex(t => t === template) ?? -1;
        if (index !== -1) {
            this.templates?.templates.splice(index, 1);
            await this.saveTemplates(false);
        } else {
            index = this.templatesSharedOwn?.templates.findIndex(t => t === template) ?? -1;
            if (index !== -1) {
                this.templatesSharedOwn?.templates.splice(index, 1);
                await this.saveTemplates(true);
            }
        }
    }

    //pivot integration
    private getPivotTableConfigJson(): string {
        let obj = window.$pvtBuilder.pivotdataWebPivotBuilder('getPivotTableConfig');

        //add cube id
        let cubeid = window.$pvtBuilder.pivotdataWebPivotBuilder('getCurrentCubeId');
        obj.CubeName = cubeid;

        //start end date relative
        let ms1970 = new Date().valueOf();
        obj.Start -= ms1970;
        obj.End -= ms1970;

        return JSON.stringify(obj);
    }

    private setPivotTableConfigJson(json: string): void {
        let config = JSON.parse(json);

        //start end date relative
        let ms1970 = new Date().valueOf();
        config.Start += ms1970;
        config.End += ms1970;

        window.$pvtBuilder.pivotdataWebPivotBuilder('setPivotTableConfig', config.CubeName, config);
        window.$pvtBuilder.pivotdataWebPivotBuilder('render');
    }

    //mappers

    //getters
    private get firstXTemplates(): PivotTemplateInfo[] {
        return this.allTemplates.slice(0, this.firstXTemplatesCount);
    }

    private get allTemplates(): PivotTemplateInfo[] {
        return (this.templates?.templates.map(e => new PivotTemplateInfo(e, true, false)) ?? [])
            .concat(this.templatesSharedOwn?.templates.map(e => new PivotTemplateInfo(e, true, true)) ?? [])
            .concat(this.templatesSharedOther?.templates.map(e => new PivotTemplateInfo(e, false, true)) ?? [])
    }
    
    get moreThanXTemplatesAvailable(): boolean {
        return this.allTemplates.length > this.firstXTemplatesCount;
    }
}

class PivotTemplateInfo {
    readonly template: PivotTemplate;
    readonly own: boolean;
    readonly shared: boolean;

    constructor(template: PivotTemplate, own: boolean, shared: boolean) {
        this.template = template;
        this.own = own;
        this.shared = shared;
    }
}